import axios from 'axios';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

/**
 * All calls hit the same field-view service Advances/Expenses already uses
 * (REACT_APP_API_APP_FIELD_URL) — Devanshu's attendance/leave/payroll module is mounted
 * on that same Express app at /attendance and /payroll (see field-view/routes/index.js:
 * app.use("/attendance", attendance_routes); app.use("/payroll", payroll_routes)).
 * Same auth convention as apiaryService.js: source: humblebee_admin + bearer token.
 */
const authHeaders = () => ({
  'Content-Type': 'application/json',
  source: 'humblebee_admin',
  Authorization: `Bearer ${cookies.get('access_token')}`,
});

const FIELD_API = () => process.env.REACT_APP_API_APP_FIELD_URL;

/**
 * Real backend limitation, not a bug in this client: getTeamAttendance only ever
 * queries by a single `date` (defaults to today) — the month/year query params it
 * accepts are destructured but never actually used in the Mongo query
 * (attendance_controller.js:221-234). There is no month-range team endpoint yet, so
 * the Attendance Log tab is built around a single date picker, not a month view.
 */
export const getTeamAttendance = async (date, projectId) => {
  try {
    const params = {};
    if (date) params.date = date;
    if (projectId && projectId !== 'ALL') params.project_id = projectId;
    const res = await axios.get(`${FIELD_API()}/attendance/team`, { headers: authHeaders(), params });
    return res.data?.records || [];
  } catch (error) {
    console.error('Error fetching team attendance:', error);
    return [];
  }
};

/**
 * Per-employee month calendar. getMyCalendar reads user_id from req.query as a fallback
 * before req.user, so an admin can pass any employee's id here — no separate admin route
 * needed (attendance_controller.js:146). Powers the "click a name to see their full
 * attendance" drawer.
 */
export const getUserCalendar = async (userId, month, year) => {
  const res = await axios.get(`${FIELD_API()}/attendance/my-calendar`, {
    headers: authHeaders(),
    params: { user_id: userId, month, year },
  });
  return res.data;
};

/**
 * Backfills a whole month for one employee in one call — every day defaults to PRESENT
 * except the dates in absentDates, which are written ABSENT. Only meant for generating
 * payroll on a past month that predates real punch data (see PayrollSlipTab.jsx); it
 * overwrites every day of that month for that employee, so it is never used for the
 * current month or for correcting a single day (use overrideAttendance for that).
 */
export const bulkMarkAttendance = async (userId, month, year, absentDates, projectId) => {
  const res = await axios.post(
    `${FIELD_API()}/attendance/bulk-mark`,
    { user_id: userId, month, year, absent_dates: absentDates, project_id: projectId || null },
    { headers: authHeaders() }
  );
  return res.data;
};

export const overrideAttendance = async (id, payload) => {
  const res = await axios.patch(`${FIELD_API()}/attendance/${id}/override`, payload, { headers: authHeaders() });
  return res.data;
};

export const getHolidays = async () => {
  try {
    const res = await axios.get(`${FIELD_API()}/attendance/holidays`, { headers: authHeaders() });
    return res.data?.holidays || [];
  } catch (error) {
    console.error('Error fetching holidays:', error);
    return [];
  }
};

export const createHoliday = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/attendance/holidays`, payload, { headers: authHeaders() });
  return res.data;
};

export const getPendingLeaves = async () => {
  try {
    const res = await axios.get(`${FIELD_API()}/attendance/leaves/pending`, { headers: authHeaders() });
    return res.data?.leaves || [];
  } catch (error) {
    console.error('Error fetching pending leaves:', error);
    return [];
  }
};

export const approveRejectLeave = async (id, status, comment) => {
  const res = await axios.patch(
    `${FIELD_API()}/attendance/leaves/${id}/approve`,
    { status, comment, approved_by_name: 'Admin' },
    { headers: authHeaders() }
  );
  return res.data;
};

export const getSalarySlip = async (userId, month, year) => {
  const res = await axios.get(`${FIELD_API()}/payroll/salary-slip/${userId}/${month}`, {
    headers: authHeaders(),
    params: { year },
  });
  return res.data;
};

/**
 * Statutory Detailed Payslip (Full TeamNest breakup with PF, ESIC, LOP, Advances, TDS)
 */
export const getDetailedSalarySlip = async (userId, month, year) => {
  const res = await axios.get(`${FIELD_API()}/payroll/detailed-slip/${userId}/${month}`, {
    headers: authHeaders(),
    params: { year },
  });
  return res.data;
};

export const finalizeDetailedSalarySlip = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/payroll/finalize-detailed-slip`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

export const getPayrollMaster = async (userId) => {
  const res = await axios.get(`${FIELD_API()}/payroll/master/${userId}`, {
    headers: authHeaders(),
  });
  return res.data;
};

export const upsertPayrollMaster = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/payroll/master`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

export const getMonthlyInput = async (userId, month, year) => {
  const res = await axios.get(`${FIELD_API()}/payroll/monthly-input/${userId}/${month}`, {
    headers: authHeaders(),
    params: { year },
  });
  return res.data;
};

export const saveMonthlyInput = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/payroll/monthly-input`, payload, {
    headers: authHeaders(),
  });
  return res.data;
};

export const listMonthlyDetailedPayslips = async (month, year) => {
  const res = await axios.get(`${FIELD_API()}/payroll/monthly-slips/${month}`, {
    headers: authHeaders(),
    params: { year },
  });
  return res.data;
};

/** Persists the (possibly admin-edited) numbers shown in the payslip review form. */
export const finalizeSalarySlip = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/payroll/finalize-slip`, payload, { headers: authHeaders() });
  return res.data;
};

export const upsertSalaryStructure = async (payload) => {
  const res = await axios.post(`${FIELD_API()}/payroll/salary-structure`, payload, { headers: authHeaders() });
  return res.data;
};

/**
 * Regularization ("I forgot to punch in on a past date, let me mark it now and have my
 * admin approve it") — real backend, landed on staging via regularization_controller.js.
 * Approving auto-writes the real Attendance record for that date (requested_status
 * HALF_DAY stays HALF_DAY, everything else becomes PRESENT — see reviewRegularization
 * in the backend controller).
 */
export const getPendingRegularizations = async () => {
  try {
    const res = await axios.get(`${FIELD_API()}/attendance/regularize/pending`, { headers: authHeaders() });
    return res.data?.regularizations || [];
  } catch (error) {
    console.error('Error fetching pending regularizations:', error);
    return [];
  }
};

export const approveRejectRegularization = async (id, status, comment) => {
  const res = await axios.patch(
    `${FIELD_API()}/attendance/regularize/${id}/review`,
    { status, comment, approved_by_name: 'Admin' },
    { headers: authHeaders() }
  );
  return res.data;
};
